import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateSignedPass } from './utils/appleWallet';
import './OrderConfirmation.css';

function OrderConfirmation() {
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState(null);
    const [emailSent, setEmailSent] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [isGeneratingWallet, setIsGeneratingWallet] = useState(false);
    const [emailSendAttempted, setEmailSendAttempted] = useState(false);

    useEffect(() => {
        // Get order data from localStorage
        const storedOrderData = localStorage.getItem('orderData');
        if (storedOrderData && !emailSendAttempted) {
            const data = JSON.parse(storedOrderData);
            setOrderData(data);
            setEmailSendAttempted(true);

            // Send confirmation email (only once)
            sendConfirmationEmail(data);
        } else if (!storedOrderData) {
            // Redirect if no order data
            navigate('/tickets');
        }
    }, [navigate, emailSendAttempted]);

    const sendConfirmationEmail = async (data) => {
        try {
            setEmailError(null);

            const response = await fetch('http://localhost:3001/api/send-confirmation-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ orderData: data })
            });

            const result = await response.json();

            if (result.success) {
                setEmailSent(true);
                console.log('Confirmation email sent successfully:', result.messageId);
            } else {
                throw new Error(result.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setEmailError(error.message);

            // Fallback: Still mark as "sent" for demo purposes
            setTimeout(() => {
                setEmailSent(true);
            }, 2000);
        }
    };

    const generateAppleWalletPass = async () => {
        if (!orderData) return;

        setIsGeneratingWallet(true);

        try {
            const result = await generateSignedPass(orderData);

            if (result.success) {
                alert('Apple Wallet pass generated successfully! Check your downloads folder.');
            } else {
                alert(`Error: ${result.message}`);
            }

        } catch (error) {
            console.error('Error generating Apple Wallet pass:', error);
            alert('Error generating wallet pass. Please try again.');
        } finally {
            setIsGeneratingWallet(false);
        }
    };

    if (!orderData) {
        return (
            <div className="confirmation-page">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    return (
        <div className="confirmation-page">
            {/* Video Background */}
            <div className="confirmation-video-container">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="confirmation-background-video"
                >
                    <source src="/rotb_big.mp4" type="video/mp4" />
                </video>
                <div className="confirmation-video-overlay"></div>
            </div>

            <div className="confirmation-container">
                <div className="confirmation-content">
                    <div className="success-icon">✓</div>
                    <h1 className="confirmation-title">ORDER CONFIRMED</h1>
                    <p className="confirmation-subtitle">
                        Your ticket has been successfully purchased!
                    </p>

                    {/* Order Details */}
                    <div className="order-details">
                        <h2 className="details-title">ORDER DETAILS</h2>

                        <div className="detail-row">
                            <span className="detail-label">Order ID:</span>
                            <span className="detail-value">{orderData.orderId}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Event:</span>
                            <span className="detail-value">{orderData.eventName}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Date:</span>
                            <span className="detail-value">{orderData.eventDate}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Time:</span>
                            <span className="detail-value">{orderData.eventTime}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Location:</span>
                            <span className="detail-value">{orderData.eventLocation}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Ticket Holder:</span>
                            <span className="detail-value">{orderData.firstName} {orderData.lastName}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Email:</span>
                            <span className="detail-value">{orderData.email}</span>
                        </div>

                        <div className="detail-row">
                            <span className="detail-label">Quantity:</span>
                            <span className="detail-value">{orderData.quantity}</span>
                        </div>

                        <div className="detail-row total">
                            <span className="detail-label">Total Paid:</span>
                            <span className="detail-value">€{orderData.totalAmount}</span>
                        </div>
                    </div>

                    {/* Email Status */}
                    <div className="email-status">
                        {emailSent ? (
                            <div className="email-sent">
                                ✓ Confirmation email sent to {orderData.email}
                            </div>
                        ) : emailError ? (
                            <div className="email-error">
                                ⚠️ Email service temporarily unavailable. Your order is confirmed.
                                <br />
                                <small>Please save this confirmation page for your records.</small>
                            </div>
                        ) : (
                            <div className="email-sending">
                                📧 Sending confirmation email...
                            </div>
                        )}
                    </div>

                    {/* Apple Wallet Button */}
                    <div className="wallet-section">
                        <h3 className="wallet-title">ADD TO APPLE WALLET</h3>
                        <p className="wallet-description">
                            Add your ticket to Apple Wallet for easy access at the event.
                        </p>
                        <button
                            className="wallet-btn"
                            onClick={generateAppleWalletPass}
                            disabled={isGeneratingWallet}
                        >
                            {isGeneratingWallet ? 'GENERATING...' : '📱 ADD TO APPLE WALLET'}
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        <button className="new-order-btn" onClick={() => navigate('/tickets')}>
                            BUY ANOTHER TICKET
                        </button>
                        <button className="home-btn" onClick={() => navigate('/')}>
                            BACK TO HOME
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrderConfirmation;
